/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 *
 * "Bu ekranı açıkla" için SAYFA KAYIT DEFTERİ: her route için kurumca hazırlanan
 * kısa, deterministik açıklama. Asistan canlı DOM özetiyle (screen-context.utils)
 * birleştirip tutarlı yanıt üretir. Ürüne özel kayıtlar `PAGE_REGISTRY[appCode]`
 * altına eklenir; eşleşme yoksa yalnızca DOM özeti kullanılır (graceful).
 */

export interface PageInfo {
  /** Ekranın amacı (1-2 cümle). */
  purpose: string;
  /** Kullanıcının yapabileceği başlıca işlemler. */
  actions?: string[];
  /** Dikkat edilecek ipuçları / sık karışan noktalar. */
  tips?: string[];
  /** Docs slug (DOCS_BASE_URL/{dil}/{ürün}/{slug}). */
  docsSlug?: string;
}

/** En özelden genele eşleşme için: tam path → PageInfo. `*` joker son segment. */
type AppPages = Record<string, PageInfo>;

const PAGE_REGISTRY: Record<string, AppPages> = {
  CORE: {
    '/dashboard': {
      purpose: 'Kurum geneli özet gösterge paneli; temel metrikler ve hızlı erişim.',
      actions: ['Metrikleri görüntüleme', 'İlgili modüllere geçiş'],
      docsSlug: 'dashboard',
    },
    '/companies': {
      purpose: 'Firmaların listelendiği ve yönetildiği ekran.',
      actions: ['Firma ekleme/düzenleme', 'Arama ve filtreleme', 'Durum değiştirme'],
      docsSlug: 'companies',
    },
    '/product-management': {
      purpose: 'Ürünlerin aktif olduğu firmaların merkezi olarak yönetildiği ekran.',
      actions: ['Ürüne firma atama', 'Firma aktivasyonlarını toplu güncelleme'],
      docsSlug: 'product-management',
    },
    '/users': {
      purpose: 'Kullanıcıların yönetildiği ekran.',
      actions: ['Kullanıcı ekleme/düzenleme', 'Rol atama', 'Aktif/pasif yapma'],
      docsSlug: 'users',
    },
    '/roles': {
      purpose: 'Rollerin ve izinlerin (RBAC) tanımlandığı ekran.',
      actions: ['Rol oluşturma', 'İzin atama'],
      docsSlug: 'roles',
    },
    '/groups': {
      purpose: 'Kullanıcı gruplarının yönetildiği ekran.',
      actions: ['Grup oluşturma', 'Üye ekleme/çıkarma', 'Grup yöneticisi atama'],
      docsSlug: 'groups',
    },
  },
};

function normalizePath(path: string): string {
  return (path || '/').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
}

/**
 * Verilen ürün ve route için kurumsal sayfa bilgisini döndürür.
 * Önce tam eşleşme, sonra üst segment eşleşmesi denenir; yoksa undefined.
 */
export function getPageInfo(appCode: string | undefined, path: string): PageInfo | undefined {
  const app = String(appCode || '').toUpperCase();
  const pages = PAGE_REGISTRY[app];
  if (!pages) return undefined;

  const norm = normalizePath(path);
  if (pages[norm]) return pages[norm];

  // Üst segment eşleşmesi: /users/42 → /users
  const segments = norm.split('/').filter(Boolean);
  while (segments.length > 0) {
    segments.pop();
    const candidate = '/' + segments.join('/');
    if (pages[candidate]) return pages[candidate];
  }
  return undefined;
}
